// Copyright (c) StrangeBee 2023

import { classList } from '@Helpers/className';
import { Menu } from 'antd';
import React, { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';

import './THVisualizer.scss';

const renderItem = <T extends { [key: string]: any }>(
    item: T,
    children: (...args: any[]) => any,
    setSelected: (...args: any[]) => any,
    itemKey: string,
): ReactElement => (
    <Menu.Item key={item[itemKey]} className="menu__item" onClick={(): void => setSelected(item)}>
        {children(item)}
    </Menu.Item>
);

interface THVisualizerProps<T extends { [key: string]: any }> {
    dataSource: T[] | Record<string, T[]>;
    children: (item: T) => ReactElement;
    title: ReactNode;
    filter: ReactNode;
    content: ReactNode;
    menuAction?: ReactNode;
    itemKey: string;
    selectedState: {
        selected: T | undefined;
        setSelected: React.Dispatch<React.SetStateAction<T | undefined>>;
    };
    className?: string;
    hideMenu?: boolean;
}

export const THVisualizer = <T extends { [key: string]: any }>({
    dataSource,
    children,
    selectedState,
    title,
    filter,
    content,
    menuAction,
    className,
    itemKey,
    hideMenu,
}: THVisualizerProps<T>): ReactElement => {
    const [openKeys, setOpenKeys] = useState<string[]>();
    const { selected, setSelected } = selectedState;

    useEffect(() => {
        if (Object.keys(dataSource).length && !openKeys) {
            setOpenKeys([Object.keys(dataSource).find(Boolean) || '']);
        }
    }, [dataSource, openKeys]);

    return (
        <div className={classList('th-visualizer', className)}>
            <div className="th-visualizer__viewer">
                <div className="th-visualizer__viewer__header">{title}</div>
                <div className="th-visualizer__viewer__content">{content}</div>
            </div>
            {!hideMenu && (
                <div className="th-visualizer__explorer">
                    <div className="th-visualizer__explorer__header">{filter}</div>
                    <Menu
                        className="th-visualizer__explorer__menu"
                        mode="inline"
                        selectedKeys={[selected?.[itemKey] || '']}
                        openKeys={openKeys}
                    >
                        {Array.isArray(dataSource)
                            ? dataSource.map((item) => renderItem(item, children, setSelected, itemKey))
                            : Object.entries(dataSource).map(([category, items]) => {
                                  return (
                                      <Menu.SubMenu
                                          className="menu__item-group"
                                          key={category}
                                          title={category}
                                          onTitleClick={(): void => {
                                              if (openKeys?.includes(category)) {
                                                  setOpenKeys(openKeys?.filter((value) => value !== category));
                                              } else {
                                                  setOpenKeys([...(openKeys || []), category]);
                                              }
                                          }}
                                      >
                                          {items.map((item) => renderItem(item, children, setSelected, itemKey))}
                                      </Menu.SubMenu>
                                  );
                              })}
                    </Menu>
                    <div className="th-visualizer__explorer__menu-actions">{menuAction}</div>
                </div>
            )}
        </div>
    );
};
